import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { minutesAPI, meetingAPI, bodyAPI, agendaAPI, aiAPI, organizationAPI, exportAPI, templateAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Save, Loader2, Sparkles, Send, CheckCircle,
  Plus, Trash2, GripVertical, ChevronDown, ChevronRight, Eye, Download, FileStack
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const MinutesEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [minute, setMinute] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [body, setBody] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(null);
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const minuteRes = await minutesAPI.get(id);
      setMinute(minuteRes.data);
      
      const existingSections = minuteRes.data.sections || [];
      
      if (minuteRes.data.meeting_id) {
        const [meetingRes, orgRes] = await Promise.all([
          meetingAPI.get(minuteRes.data.meeting_id),
          user?.organization_id ? organizationAPI.get(user.organization_id) : Promise.resolve({ data: null }),
        ]);
        setMeeting(meetingRes.data);
        setOrganization(orgRes.data);
        
        if (meetingRes.data.body_id) {
          const bodyRes = await bodyAPI.get(meetingRes.data.body_id);
          setBody(bodyRes.data);
        }

        // Build sections from agenda items if no existing sections
        if (existingSections.length === 0) {
          const agendaRes = await agendaAPI.getByMeeting(minuteRes.data.meeting_id);
          const built = buildSectionsFromAgenda(agendaRes.data, meetingRes.data);
          setSections(built);
          const expanded = {};
          built.forEach(s => { expanded[s.id] = true; });
          setExpandedSections(expanded);
        } else {
          setSections(existingSections);
          const expanded = {};
          existingSections.forEach(s => { expanded[s.id] = true; });
          setExpandedSections(expanded);
        }
      } else {
        setSections(existingSections.length > 0 ? existingSections : [createBlankSection(0)]);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load minutes');
    } finally {
      setLoading(false);
    }
  };

  const buildSectionsFromAgenda = (agendaItems, mtg) => {
    const sects = [
      {
        id: 'call_to_order',
        title: 'Call to Order',
        content: `The meeting was called to order at ${mtg?.date_time ? new Date(mtg.date_time).toLocaleTimeString() : '___'} by the Presiding Officer.`,
        order: 0,
        type: 'standard',
      },
      {
        id: 'certification_quorum',
        title: 'Certification of Quorum',
        content: 'The Corporate Secretary certified the existence of a quorum for the transaction of business.',
        order: 1,
        type: 'standard',
      },
    ];

    if (agendaItems && agendaItems.length > 0) {
      agendaItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      agendaItems.forEach((item, idx) => {
        sects.push({
          id: item.id || `agenda_${idx}`,
          title: item.title,
          content: item.notes || '',
          order: idx + 2,
          type: item.item_type || 'discussion',
          agenda_item_id: item.id,
        });
      });
    }

    sects.push({
      id: 'adjournment',
      title: 'Adjournment',
      content: 'There being no other matters to discuss, the meeting was adjourned.',
      order: sects.length,
      type: 'standard',
    });

    return sects;
  };

  const createBlankSection = (order) => ({
    id: `section_${Date.now()}`,
    title: 'New Section',
    content: '',
    order,
    type: 'standard',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await minutesAPI.update(id, { sections, updated_at: new Date().toISOString() });
      toast.success('Minutes saved');
    } catch (err) {
      toast.error('Failed to save minutes');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      await minutesAPI.update(id, { status: newStatus, sections });
      setMinute({ ...minute, status: newStatus });
      toast.success(`Minutes ${newStatus.replace('_', ' ')}`);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (sectionId, field, value) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, [field]: value } : s));
  };

  const addSection = (afterIndex) => {
    const newSect = createBlankSection(afterIndex + 1);
    setSections(prev => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, newSect);
      return next.map((s, i) => ({ ...s, order: i }));
    });
    setExpandedSections(prev => ({ ...prev, [newSect.id]: true }));
  };

  const removeSection = (sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId).map((s, i) => ({ ...s, order: i })));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const generateAIForSection = async (sectionId) => {
    if (!meeting) return;
    setGeneratingAI(sectionId);
    try {
      const section = sections.find(s => s.id === sectionId);
      const response = await aiAPI.draftMinutes(meeting.id);
      if (response.data?.draft) {
        updateSection(sectionId, 'content', response.data.draft);
        toast.success('AI draft generated');
      }
    } catch (err) {
      toast.error('AI generation failed');
    } finally {
      setGeneratingAI(null);
    }
  };

  const generateAllAI = async () => {
    if (!meeting) return;
    setGeneratingAI('all');
    try {
      const response = await aiAPI.draftMinutes(meeting.id);
      if (response.data?.draft) {
        setSections(prev => prev.map(s => {
          if (s.type === 'standard') return s;
          return { ...s, content: response.data.draft };
        }));
        toast.success('AI draft generated for all sections — please review before approving');
      }
    } catch (err) {
      toast.error('AI generation failed');
    } finally {
      setGeneratingAI(null);
    }
  };

  const generateFromTemplate = async () => {
    setGeneratingTemplate(true);
    try {
      const response = await templateAPI.generateFromTemplate({
        template_type: 'minutes',
        meeting_id: meeting?.id,
      });
      if (response.data?.found && response.data?.html) {
        const templateSection = {
          id: `template_${Date.now()}`,
          title: `From Template: ${response.data.template_name}`,
          content: response.data.html,
          order: 0,
          type: 'template',
        };
        setSections(prev => {
          const updated = [templateSection, ...prev.map((s, i) => ({ ...s, order: i + 1 }))];
          return updated;
        });
        setExpandedSections(prev => ({ ...prev, [templateSection.id]: true }));
        toast.success(`Template "${response.data.template_name}" applied with your meeting data`);
      } else {
        toast.info('No template found for minutes. Ask your admin to set up a default template.');
      }
    } catch (err) {
      toast.error('Failed to generate from template');
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const getStatusActions = () => {
    if (!minute) return [];
    switch (minute.status) {
      case 'draft': return [{ label: 'Submit for Review', status: 'under_review', icon: Send }];
      case 'under_review': return [{ label: 'Ready for Approval', status: 'for_approval', icon: CheckCircle }];
      case 'for_approval': return [{ label: 'Approve Minutes', status: 'approved', icon: CheckCircle }];
      default: return [];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  const statusActions = getStatusActions();
  const aiEnabled = organization?.ai_enabled && organization?.ai_minutes_enabled !== false;

  return (
    <div className="space-y-6" data-testid="minutes-editor">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/minutes')} data-testid="back-to-minutes">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#1A1D21]">{minute?.title || 'Minutes Editor'}</h1>
            <div className="flex items-center gap-3 mt-1">
              {meeting && (
                <span className="text-sm text-[#5A6570]">{meeting.title}</span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded border ${
                minute?.status === 'approved' ? 'bg-green-100 text-green-800 border-green-300' :
                minute?.status === 'for_approval' ? 'bg-cyan-100 text-cyan-800 border-cyan-300' :
                minute?.status === 'under_review' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                'bg-amber-100 text-amber-800 border-amber-300'
              }`}>
                {minute?.status?.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
            className="border-[#E2E6EA]"
            data-testid="toggle-preview"
          >
            <Eye className="h-4 w-4 mr-2" />
            {previewMode ? 'Edit' : 'Preview'}
          </Button>

          <a
            href={exportAPI.minutesPdf(id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex"
          >
            <Button variant="outline" size="sm" className="border-[#E2E6EA]" data-testid="export-pdf">
              <Download className="h-4 w-4 mr-2" />PDF
            </Button>
          </a>

          {!previewMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={generateFromTemplate}
              disabled={generatingTemplate}
              className="border-[#F58A26] text-[#F58A26]"
              data-testid="generate-from-template"
            >
              {generatingTemplate ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileStack className="h-4 w-4 mr-2" />}
              From Template
            </Button>
          )}

          {aiEnabled && !previewMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={generateAllAI}
              disabled={generatingAI === 'all'}
              className="border-[#00A3A3] text-[#00A3A3]"
              data-testid="generate-all-ai"
            >
              {generatingAI === 'all' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Generate All with AI
            </Button>
          )}

          <Button onClick={handleSave} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-minutes">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>

          {statusActions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="border-[#F58A26] text-[#F58A26]" data-testid="workflow-actions">
                  Workflow
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {statusActions.map(action => (
                  <DropdownMenuItem key={action.status} onClick={() => handleStatusChange(action.status)}>
                    <action.icon className="h-4 w-4 mr-2" />
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* AI Draft Disclaimer */}
      {aiEnabled && (
        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="ai-disclaimer">
          <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>AI Drafting Aid:</strong> AI-generated content is a draft only. The Corporate Secretary must review, edit, and formally approve all minutes before they become official corporate records per RCC Sec 73.
          </p>
        </div>
      )}

      {/* Preview Mode */}
      {previewMode ? (
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-8 prose max-w-none">
            <h2 className="text-center text-[#1A1D21] mb-6">{minute?.title}</h2>
            {meeting && (
              <p className="text-center text-[#5A6570] text-sm mb-8">
                {body?.name} | {new Date(meeting.date_time).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
            {sections.map(section => (
              <div key={section.id} className="mb-6">
                <h3 className="text-lg font-semibold text-[#1A1D21] mb-2">{section.title}</h3>
                <div className="text-[#3A3F45] whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: section.content || '<em>No content</em>' }} />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        /* Editor Mode */
        <div className="space-y-3">
          {sections.map((section, idx) => (
            <Card key={section.id} className="border-[#E2E6EA]" data-testid={`section-${section.id}`}>
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#F8F9FA] border-b border-[#E2E6EA]"
                onClick={() => toggleSection(section.id)}
              >
                <GripVertical className="h-4 w-4 text-[#A8B1B9]" />
                {expandedSections[section.id] ? (
                  <ChevronDown className="h-4 w-4 text-[#5A6570]" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-[#5A6570]" />
                )}
                <Input
                  value={section.title}
                  onChange={(e) => { e.stopPropagation(); updateSection(section.id, 'title', e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  className="border-none p-0 h-auto font-semibold text-[#1A1D21] focus-visible:ring-0 bg-transparent"
                />
                <div className="flex items-center gap-1 ml-auto">
                  {aiEnabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); generateAIForSection(section.id); }}
                      disabled={generatingAI === section.id}
                      className="text-[#00A3A3] h-7"
                      data-testid={`ai-section-${section.id}`}
                    >
                      {generatingAI === section.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); removeSection(section.id); }}
                    className="text-red-500 h-7"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              
              {expandedSections[section.id] && (
                <CardContent className="p-4">
                  {section.type === 'template' ? (
                    <div
                      className="min-h-[120px] border border-[#E2E6EA] rounded-md p-3 text-sm prose max-w-none focus:outline-none focus:ring-2 focus:ring-[#00A3A3]/40"
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateSection(section.id, 'content', e.currentTarget.innerHTML)}
                      dangerouslySetInnerHTML={{ __html: section.content }}
                      data-testid={`content-${section.id}`}
                    />
                  ) : (
                    <Textarea
                      value={section.content}
                      onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                      placeholder="Enter minutes content for this section..."
                      className="min-h-[120px] border-[#E2E6EA] text-sm"
                      data-testid={`content-${section.id}`}
                    />
                  )}
                  <div className="flex justify-end mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => addSection(idx)}
                      className="text-[#00A3A3] text-xs"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add Section Below
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MinutesEditor;
